import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, Maximize2, Minus, Navigation, Plus } from 'lucide-react';
import type { MapHoverConfig } from './types';
import { resolveAddressString, getStaticMapUrl, getDirectionsUrl, placePreview, maxPreviewZoom, PREVIEW_BOX, PREVIEW_ZOOM, type StaticMapType } from './utils';

interface MapPreviewTooltipProps {
  config: MapHoverConfig | null;
  triggerRect: DOMRect | null;
  /** Opens the full interactive map panel anchored to the hovered element. Omitted when the caller didn't supply an `expandConfig`. */
  onExpand?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export function MapPreviewTooltip({ config, triggerRect, onExpand, onMouseEnter, onMouseLeave }: MapPreviewTooltipProps) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  /**
   * The person's chosen view, tagged with the address it was chosen for. A different address reads as the default view in the SAME render
   * (no effect that resets it a render late, which asked Google for one wasted picture at the old zoom).
   */
  const [chosen, setChosen] = useState<{ address: string; zoom: number; mapType: StaticMapType }>({ address: '', zoom: PREVIEW_ZOOM.initial, mapType: 'roadmap' });
  /** The picture address that failed to load; a different view is a different address, so it is tried again without any reset. */
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  const addressStr = config ? resolveAddressString(config.address) : '';
  const view = chosen.address === addressStr ? chosen : { address: addressStr, zoom: PREVIEW_ZOOM.initial, mapType: 'roadmap' as StaticMapType };
  const { zoom, mapType } = view;

  if (!config || !triggerRect || !apiKey) return null;
  if (!addressStr) return null;

  const tooltipWidth = PREVIEW_BOX.width;
  const mapHeight = PREVIEW_BOX.mapHeight;
  const actionsHeight = PREVIEW_BOX.actionsHeight;
  const tooltipHeight = mapHeight + actionsHeight;
  const gap = 8;

  // All of the preview stays in the window wherever the pin is on the screen or scrolled to (ISS-637).
  const { left, top } = placePreview(
    triggerRect,
    { width: tooltipWidth, height: tooltipHeight },
    { width: window.innerWidth, height: window.innerHeight },
    gap
  );

  const staticMapSrc = getStaticMapUrl(addressStr, apiKey, tooltipWidth, mapHeight, zoom, mapType);
  const imgError = failedSrc === staticMapSrc;
  const maxZoom = maxPreviewZoom(mapType);
  const zoomBy = (delta: number) => setChosen({ ...view, zoom: Math.min(maxZoom, Math.max(PREVIEW_ZOOM.min, zoom + delta)) });
  const toggleSatellite = () => {
    const next: StaticMapType = mapType === 'satellite' ? 'roadmap' : 'satellite';
    setChosen({ ...view, mapType: next, zoom: Math.min(zoom, maxPreviewZoom(next)) });
  };

  // aria-disabled (not disabled) at a limit: a disabled button drops keyboard focus to the page in the middle of zooming.
  const controlButton =
    'flex h-7 w-7 items-center justify-center rounded bg-white/95 text-slate-700 shadow ring-1 ring-slate-300 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 aria-disabled:opacity-40';

  const handleDirections = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(getDirectionsUrl(addressStr), '_blank', 'noopener,noreferrer');
  };

  const handleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    onExpand?.();
  };

  return createPortal(
    <div
      className="fixed z-[60] flex flex-col rounded-lg shadow-xl border border-slate-200 overflow-hidden bg-white"
      style={{ left, top, width: tooltipWidth, height: tooltipHeight }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      // The tooltip is a React-tree descendant of the card's clickable outer
      // div (via portal), so any click here that isn't stopped would bubble
      // through React's synthetic event system and open the lead. Only the
      // two buttons stop propagation themselves; this catches clicks on the
      // map image, background, and divider so the card never opens
      // accidentally when interacting with the tooltip.
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ height: mapHeight }} className="relative flex-shrink-0">
        {imgError ? (
          <div className="w-full h-full bg-slate-100 flex flex-col items-center justify-center">
            <MapPin className="w-6 h-6 text-slate-400 mb-1" />
            <span className="text-xs text-slate-500 text-center px-2">{addressStr}</span>
          </div>
        ) : (
          <img
            src={staticMapSrc}
            alt={`Map preview of ${addressStr}`}
            width={tooltipWidth}
            height={mapHeight}
            className="block w-full h-full object-cover"
            onError={() => setFailedSrc(staticMapSrc)}
          />
        )}
        <div className="absolute right-1 top-1 flex flex-col gap-1" role="group" aria-label="Zoom">
          <button type="button" className={controlButton} aria-label="Zoom in" title="Zoom in" aria-disabled={zoom >= maxZoom} onClick={() => zoomBy(1)}>
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
          <button type="button" className={controlButton} aria-label="Zoom out" title="Zoom out" aria-disabled={zoom <= PREVIEW_ZOOM.min} onClick={() => zoomBy(-1)}>
            <Minus className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
        {/* Top-left, not bottom-left: Google draws its logo at the bottom-left of the picture and the terms forbid covering it. */}
        <button
          type="button"
          className={`absolute left-1 top-1 rounded px-2 py-1 text-[11px] font-semibold shadow ring-1 ring-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${
            mapType === 'satellite' ? 'bg-sky-600 text-white' : 'bg-white/95 text-slate-700 hover:bg-white'
          }`}
          aria-label="Satellite view"
          aria-pressed={mapType === 'satellite'}
          title="Satellite view"
          onClick={toggleSatellite}
        >
          Satellite
        </button>
      </div>
      <div
        className="flex items-center justify-center gap-1 border-t border-slate-200 bg-white flex-shrink-0"
        style={{ height: actionsHeight }}
      >
        {onExpand && (
          <button
            onClick={handleExpand}
            className="flex-1 flex items-center justify-center gap-1.5 h-full text-xs font-medium text-slate-600 hover:bg-slate-50 hover:text-sky-600 transition-colors"
            aria-label="Expand map"
            title="Expand map"
          >
            <Maximize2 className="w-3.5 h-3.5" />
            Expand
          </button>
        )}
        {onExpand && <div className="w-px h-4 bg-slate-200" aria-hidden="true" />}
        <button
          onClick={handleDirections}
          className="flex-1 flex items-center justify-center gap-1.5 h-full text-xs font-medium text-slate-600 hover:bg-slate-50 hover:text-sky-600 transition-colors"
          aria-label="Get directions from my current location"
          title="Get directions from my current location"
        >
          <Navigation className="w-3.5 h-3.5" />
          Directions
        </button>
      </div>
    </div>,
    document.body,
  );
}
